import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

type State =
  | { kind: 'loading' }
  | { kind: 'invalid'; message: string }
  | { kind: 'already' }
  | { kind: 'confirm' }
  | { kind: 'success' }
  | { kind: 'error'; message: string }

function UnsubscribePage() {
  const search = typeof window !== 'undefined' ? window.location.search : ''
  const token = new URLSearchParams(search).get('token') ?? ''
  const [state, setState] = useState<State>({ kind: 'loading' })

  useEffect(() => {
    if (!token) {
      setState({ kind: 'invalid', message: 'Missing unsubscribe token.' })
      return
    }
    ;(async () => {
      try {
        const res = await fetch(`/email/unsubscribe?token=${encodeURIComponent(token)}`)
        const data = await res.json()
        if (!res.ok) {
          setState({ kind: 'invalid', message: data?.error ?? 'Invalid or expired link.' })
          return
        }
        if (data.valid === false && data.reason === 'already_unsubscribed') {
          setState({ kind: 'already' })
          return
        }
        setState({ kind: 'confirm' })
      } catch {
        setState({ kind: 'error', message: 'Could not validate the link. Please try again.' })
      }
    })()
  }, [token])

  async function confirm() {
    setState({ kind: 'loading' })
    try {
      const res = await fetch('/email/unsubscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })
      const data = await res.json()
      if (!res.ok) {
        setState({ kind: 'error', message: data?.error ?? 'Failed to unsubscribe.' })
        return
      }
      if (data.success) setState({ kind: 'success' })
      else if (data.reason === 'already_unsubscribed') setState({ kind: 'already' })
      else setState({ kind: 'error', message: 'Failed to unsubscribe.' })
    } catch {
      setState({ kind: 'error', message: 'Network error. Please try again.' })
    }
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-sm text-center">
        <h1 className="text-2xl font-display text-primary mb-3">Unsubscribe</h1>
        {state.kind === 'loading' && <p className="text-muted-foreground">Checking your link…</p>}
        {state.kind === 'invalid' && <p className="text-destructive">{state.message}</p>}
        {state.kind === 'error' && <p className="text-destructive">{state.message}</p>}
        {state.kind === 'already' && (
          <p className="text-muted-foreground">You're already unsubscribed. You won't receive further emails.</p>
        )}
        {state.kind === 'confirm' && (
          <>
            <p className="text-muted-foreground mb-6">
              Click below to confirm you no longer want to receive emails from Bacha Way Travel.
            </p>
            <button
              onClick={confirm}
              className="inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground px-6 py-3 font-medium hover:opacity-90 transition"
            >
              Confirm unsubscribe
            </button>
          </>
        )}
        {state.kind === 'success' && (
          <p className="text-primary">You have been unsubscribed. We're sorry to see you go.</p>
        )}
      </div>
    </div>
  )
}

export const Route = createFileRoute('/unsubscribe')({
  component: UnsubscribePage,
})
